import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const QUICK_TEMPLATES = [
  // ★ Address template — staff was typing this verbatim 20+ times a day.
  //   Now it's one click. Owner-requested.
  { label: "📍 Address", text: "Our location is 1451 old country rd Plainview NY 11803" },
  { label: "Appointment Reminder", text: "Hi {name}! Friendly reminder for your appointment tomorrow. See you soon!" },
  { label: "Thank You", text: "Thank you for visiting today, {name}! Hope you loved your treatment." },
  { label: "Follow-up", text: "Hi {name}! Checking in on how you are feeling after your treatment." },
];

function formatPhone(p) {
  if (!p) return "";
  const cleaned = p.replace(/\D/g, "");
  if (cleaned.length === 10) return "(" + cleaned.slice(0,3) + ") " + cleaned.slice(3,6) + "-" + cleaned.slice(6);
  if (cleaned.length === 11) return "+1 (" + cleaned.slice(1,4) + ") " + cleaned.slice(4,7) + "-" + cleaned.slice(7);
  return p;
}

// Last-10-digits key for phone comparisons (handles +1 / no prefix / formatting)
function phoneKey(p) {
  if (!p) return "";
  return p.replace(/\D/g, "").slice(-10);
}

// Some inbound messages got stored as JSON strings like
// `{"type":11,"body":"Full name: ...\nPhone number: ..."}` — unpack to clean text.
function cleanBody(body) {
  if (!body || typeof body !== "string") return body || "";
  const trimmed = body.trim();
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && parsed.body) {
        return String(parsed.body);
      }
    } catch (_) { /* not valid JSON, fall through */ }
  }
  return body;
}

function timeAgo(date) {
  const now = new Date();
  const past = new Date(date);
  const diff = Math.floor((now - past) / 1000);
  if (diff < 60) return "now";
  if (diff < 3600) return Math.floor(diff/60) + "m";
  if (diff < 86400) return Math.floor(diff/3600) + "h";
  if (diff < 604800) return Math.floor(diff/86400) + "d";
  return past.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Friendly clock for the bubble — "2:30 PM", "Yesterday 8:14 PM", "May 27 at 3:00 PM"
function bubbleTime(date) {
  const d = new Date(date);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  if (sameDay) return time;
  if (isYesterday) return "Yesterday " + time;
  const ageDays = Math.floor((now - d) / 86400000);
  if (ageDays < 7) return d.toLocaleDateString("en-US", { weekday: "short" }) + " " + time;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " " + time;
}

export default function MessagesInbox({ clients, openClientProfile }) {
  const [conversations, setConversations] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [search, setSearch] = useState("");
  // "all" shows everything with an UNREAD / READ divider between sections.
  // "unread" filters to just unread threads.
  const [readFilter, setReadFilter] = useState("all"); // 'all' | 'unread'
  // ★ Category tabs requested by owner — separates the firehose into:
  //   • "all"           — everything except archived/blocked
  //   • "leads"         — no linked client (lead_id only OR unknown sender)
  //   • "active"        — has client_id (existing client conversations)
  //   • "confirmations" — has a daily_confirmation message in the last 48h.
  //                        Lets staff burn through all confirmation replies
  //                        in one focused view instead of opening each
  //                        client conversation individually.
  //   • "archive"       — status === 'archived' OR 'blocked' (STOP opt-out)
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [newName, setNewName] = useState("");
  const [newFromClient, setNewFromClient] = useState("");
  const [newSearchText, setNewSearchText] = useState("");
  // Lead matches (for New Message search) — fetched on demand from `leads`
  // table when the search text changes. Without this, leads created by the
  // cold-outreach campaign (Ingrid, Elena, etc.) can't be messaged from the
  // New Message dialog because they're not in the clients table.
  const [newLeadMatches, setNewLeadMatches] = useState([]);
  const [newLeadSearching, setNewLeadSearching] = useState(false);

  useEffect(() => {
    const q = newSearchText.trim();
    if (q.length < 2) { setNewLeadMatches([]); return; }
    let cancelled = false;
    setNewLeadSearching(true);
    const t = setTimeout(async () => {
      const digits = q.replace(/\D/g, "");
      let query = supabase
        .from("leads")
        .select("id,full_name,first_name,phone,email,source,source_detail")
        .not("phone", "is", null);
      if (digits.length >= 4) {
        query = query.or(`full_name.ilike.%${q}%,first_name.ilike.%${q}%,email.ilike.%${q}%,phone.ilike.%${digits}%`);
      } else {
        query = query.or(`full_name.ilike.%${q}%,first_name.ilike.%${q}%,email.ilike.%${q}%`);
      }
      const { data } = await query.limit(8);
      if (!cancelled) {
        setNewLeadMatches(data || []);
        setNewLeadSearching(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [newSearchText]);
  const [showTemplates, setShowTemplates] = useState(false);
  const scrollRef = useRef(null);

  // AI auto-reply toggle for the open conversation
  const [aiOn, setAiOn] = useState(true);
  const [convId, setConvId] = useState(null);

  // Re-engagement preview modal
  const [showReeng, setShowReeng] = useState(false);
  const [reengLoading, setReengLoading] = useState(false);
  const [reengPreview, setReengPreview] = useState(null);
  const [reengSending, setReengSending] = useState(false);
  const [reengResult, setReengResult] = useState(null);

  useEffect(() => {
    loadMessages();
    // ★ PERFORMANCE — owner complained about the inbox being slow to load
    //   and stuck during realtime events. Two changes:
    //
    //   1. Patch in-place on realtime instead of full refetch. With 4,700+
    //      bot_messages a full reload pulls ~1-2 MB over the wire EVERY
    //      time an SMS arrives. In-place patching is ~free.
    //
    //   2. Only fall back to a full reload as a heavy-handed safety net
    //      every 60 seconds (catches anything we miss).
    let safetyTimer = null;
    const queueSafetyReload = () => {
      if (safetyTimer) return;
      safetyTimer = setTimeout(() => { safetyTimer = null; loadMessages(); }, 60_000);
    };

    const patchInsertBotMessage = (row) => {
      // Map a raw bot_messages row → the shape the inbox uses
      setAllMessages(prev => {
        // De-dupe if the same id is already there
        const id = "bot_" + row.id;
        if (prev.some(m => m.id === id)) return prev;
        const mapped = mapBotRowToMsg(row);
        if (!mapped) return prev;
        // Insert sorted (ascending by created_at). Append for the common case
        // where the new row IS newer than everything we have.
        const next = [...prev, mapped];
        next.sort((a,b) => new Date(a.created_at) - new Date(b.created_at));
        return next;
      });
    };

    const patchUpdateBotMessage = (row) => {
      setAllMessages(prev => prev.map(m => m.id === ("bot_" + row.id)
        ? { ...m, body: row.message_text, read: row.read ?? m.read }
        : m));
    };

    const channel = supabase.channel("messages-inbox-" + Date.now())
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "bot_messages" }, (payload) => {
        patchInsertBotMessage(payload.new);
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "bot_messages" }, (payload) => {
        patchUpdateBotMessage(payload.new);
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "bot_conversations" }, queueSafetyReload)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "bot_conversations" }, queueSafetyReload)
      .subscribe();
    return () => {
      if (safetyTimer) clearTimeout(safetyTimer);
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper: convert a raw bot_messages row into the inbox's normalized msg
  // shape. Used both by realtime patching and by loadMessages — keeping the
  // mapping in one place prevents drift.
  const mapBotRowToMsg = (bm) => {
    const conv = convMapRef.current.get(bm.conversation_id) || {};
    const phone = conv.phone || "";
    const cid = conv.client_id || null;
    const lid = conv.lead_id || null;
    const convStatus = conv.status || "active";
    const clientsByPhone = clientsByPhoneRef.current;
    const clientFromState = cid ? clients.find(c => c.id === cid) : null;
    const clientFromPhone = !clientFromState && phone ? clientsByPhone.get(phoneKey(phone)) : null;
    const resolvedClient = clientFromState || clientFromPhone;
    const clientName = resolvedClient?.name || (phone ? formatPhone(phone) : "");
    const toolName = Array.isArray(bm.ai_tools_used) && bm.ai_tools_used[0]?.name;
    let src = bm.sender === "staff" ? "staff"
            : bm.sender === "ai" ? "ai"
            : bm.sender === "system" ? "system"
            : "client";
    if (toolName === "reengagement") src = "reengagement";
    else if (typeof toolName === "string" && toolName.startsWith("daily_confirmation")) src = "confirmation";
    else if (typeof toolName === "string" && toolName.startsWith("cold_outreach")) src = "cold_outreach";
    return {
      id: "bot_" + bm.id,
      client_id: cid || resolvedClient?.id || null,
      lead_id: lid,
      conv_status: convStatus,
      client_name: clientName,
      from_number: bm.direction === "inbound" ? phone : null,
      to_number: bm.direction === "outbound" ? phone : null,
      direction: bm.direction,
      body: bm.message_text,
      created_at: bm.created_at,
      read: bm.direction === "outbound" ? true : (bm.read ?? false),
      _botId: bm.id,
      _conversationId: bm.conversation_id,
      sent_by_name: bm.sender === "ai" ? "Sophia (AI)"
                  : bm.sender === "staff" ? "Staff"
                  : bm.sender === "system" ? "Auto-System"
                  : null,
      _source: src,
    };
  };

  // Refs for the realtime patcher — useState would force re-renders when
  // we mutate these on every load. We want fast lookups without triggering
  // React re-renders.
  const convMapRef = useRef(new Map());
  const clientsByPhoneRef = useRef(new Map());

  // Keep the phone→client lookup ref in sync with the clients prop so the
  // realtime mapper resolves names correctly when a new client gets added
  // between full reloads.
  useEffect(() => {
    const m = new Map();
    (clients || []).forEach(c => { const k = phoneKey(c.phone); if (k) m.set(k, c); });
    clientsByPhoneRef.current = m;
  }, [clients]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [selectedMessages]);

  const loadMessages = async () => {
    // ★ PERFORMANCE — owner reported the inbox is very slow to load.
    //   Root cause: pulling ALL bot_messages (~4,700+) with `select("*")`
    //   on every page open + every realtime event. Total payload was
    //   ~1-2 MB per refresh and the in-React aggregation was O(N) on
    //   every message change.
    //
    //   Fixes here (in order of impact):
    //     • Time-window bot_messages to last 90 days — covers every
    //       active conversation; old archives don't belong in the sidebar
    //     • Cap at 3,000 rows max (was 20,000)
    //     • Select only the columns we actually use — drop metadata,
    //       delivered, twilio fields we never read
    //     • Drop the empty `messages` table query entirely (0 rows in DB)
    //     • Paginate bot_conversations the same way as before — needed
    //       so every phone is in the map for in-memory client lookup
    //
    //   When a staff member opens a specific conversation, the right pane
    //   already filters in memory; if they need older history (>90 days)
    //   the lazy loader below pulls just that conversation's history on
    //   demand.
    const NINETY_DAYS_AGO_ISO = new Date(Date.now() - 90 * 86400000).toISOString();

    const fetchBotMessages = async () => {
      const all = [];
      const CHUNK = 1000;
      let from = 0;
      while (true) {
        const { data, error } = await supabase
          .from("bot_messages")
          .select("id,conversation_id,direction,sender,message_text,created_at,read,ai_tools_used,twilio_sid")
          .gte("created_at", NINETY_DAYS_AGO_ISO)
          .order("created_at", { ascending: false })
          .range(from, from + CHUNK - 1);
        if (error) { console.error("[Inbox] bot_messages fetch error:", error); break; }
        if (!data || data.length === 0) break;
        all.push(...data);
        if (data.length < CHUNK) break;
        from += CHUNK;
        if (from > 3000) break;
      }
      return all;
    };

    const fetchAllConvos = async () => {
      const out = [];
      const CHUNK = 1000;
      let from = 0;
      while (true) {
        const { data, error } = await supabase
          .from("bot_conversations")
          .select("id,phone,client_id,lead_id,status")
          .order("id", { ascending: false })
          .range(from, from + CHUNK - 1);
        if (error || !data || data.length === 0) break;
        out.push(...data);
        if (data.length < CHUNK) break;
        from += CHUNK;
        if (from > 10000) break;
      }
      return out;
    };

    const [botData, convAll] = await Promise.all([
      fetchBotMessages(),
      fetchAllConvos(),
    ]);

    const manual = []; // `messages` table is unused / empty

    // bot_messages doesn't carry phone/client_id directly — pull from the parent conversation
    const convMap = new Map();
    convAll.forEach(c => convMap.set(c.id, c));
    // Cache for realtime patcher — populated once per load
    convMapRef.current = convMap;
    const cbp = new Map();
    (clients || []).forEach(c => { const k = phoneKey(c.phone); if (k) cbp.set(k, c); });
    clientsByPhoneRef.current = cbp;

    // Use the shared mapping helper so realtime patches and the initial
    // load produce identical row shapes. .filter(Boolean) drops any rows
    // whose conv is missing from the map (shouldn't happen but defensive).
    const bot = botData.map(bm => mapBotRowToMsg(bm)).filter(Boolean);

    // Sort once, ASCENDING so the per-conversation thread renders oldest→newest.
    // The conversation LIST is sorted DESC by lastMessage in the next effect.
    const all = [...manual, ...bot].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    setAllMessages(all);
  };

  // ★ LEAD NAME LOOKUP — cold-outreach / campaign repliers are LEADS, not
  //   clients. Without this map their conversations showed as a bare phone
  //   number, and searching "Rachael" found nothing (owner report
  //   2026-06-11). Load a minimal phone→name index from the leads table.
  const [leadsByPhone, setLeadsByPhone] = useState(new Map());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const map = new Map();
      let from = 0;
      const PAGE = 1000;
      while (true) {
        const { data } = await supabase.from("leads")
          .select("id,full_name,first_name,phone")
          .not("phone", "is", null)
          .range(from, from + PAGE - 1);
        if (!data || data.length === 0) break;
        data.forEach(l => {
          const k = phoneKey(l.phone);
          const nm = (l.full_name || l.first_name || "").trim();
          if (k && nm && !map.has(k)) map.set(k, { id: l.id, name: nm });
        });
        if (data.length < PAGE) break;
        from += PAGE;
      }
      if (!cancelled) setLeadsByPhone(map);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Pre-index clients by phone (last 10 digits) for fast lookup
    const clientsByPhone = new Map();
    (clients || []).forEach(c => {
      const k = phoneKey(c.phone);
      if (k) clientsByPhone.set(k, c);
    });

    const convos = {};
    allMessages.forEach(m => {
      const phone = (m.direction === "outbound" ? m.to_number : m.from_number) || "";
      const pk = phoneKey(phone);
      // Group by phone first; if phone is missing, fall back to the bot
      // conversation_id (every bot_message has one) so the entry is still
      // selectable from the sidebar. cid_ is a last-ditch fallback for
      // legacy rows with neither.
      const key = pk
        || (m._conversationId ? "conv_" + m._conversationId : null)
        || ("cid_" + (m.client_id || "unknown"));

      const matched = pk ? clientsByPhone.get(pk) : null;
      const leadMatch = !matched && pk ? leadsByPhone.get(pk) : null;
      // Priority: real client match by phone → LEAD match by phone (campaign
      // repliers!) → message's own client_name (skipping junk like "Test
      // User") → formatted phone → "Patient (no contact)"
      const name = matched?.name
        || leadMatch?.name
        || ((m.client_name && !/^test user$/i.test(m.client_name)) ? m.client_name : null)
        || (phone ? formatPhone(phone) : "Patient (no contact on file)");

      if (!convos[key]) {
        convos[key] = {
          key,
          client_id: matched?.id || (pk ? null : m.client_id),
          // ★ lead_id + conv_status flow from the most recent message that
          //   has them (they live on the conversation row, not on each msg).
          lead_id: m.lead_id || null,
          conv_status: m.conv_status || "active",
          client_name: name,
          phone,
          conversationId: m._conversationId || null,
          lastMessage: m,
          unread: 0,
          // ★ Confirmation-bucket signals (computed below as we walk messages)
          hasRecentConfirmation: false, // saw daily_confirmation in last 48h
          hasInboundReplyAfterConfirmation: false, // client responded to it
        };
      } else {
        // Upgrade name/client_id if a later message gives us a better match
        if (matched && !convos[key].client_id) {
          convos[key].client_id = matched.id;
          convos[key].client_name = matched.name;
        }
        if (!convos[key].conversationId && m._conversationId) convos[key].conversationId = m._conversationId;
        // Latest seen wins for lead_id + status (they shouldn't change but
        // be defensive in case the bot links a client later)
        if (m.lead_id && !convos[key].lead_id) convos[key].lead_id = m.lead_id;
        if (m.conv_status) convos[key].conv_status = m.conv_status;
      }
      if (new Date(m.created_at) > new Date(convos[key].lastMessage.created_at)) {
        convos[key].lastMessage = m;
      }
      if (m.direction === "inbound" && !m.read) convos[key].unread++;
      // ★ Confirmations bucket signal: any daily_confirmation message in the
      //   last 48h flags this conversation as a confirmation flow. The 48h
      //   window keeps the bucket tight to the current confirmation cycle —
      //   yesterday's reminders + today's replies all cluster here.
      const ageMs = Date.now() - new Date(m.created_at).getTime();
      const within48h = ageMs <= 48 * 60 * 60 * 1000;
      if (within48h && m._source === "confirmation") {
        convos[key].hasRecentConfirmation = true;
        convos[key].lastConfirmationAt = m.created_at;
      }
      // Track whether an inbound came AFTER the confirmation went out
      if (within48h && m.direction === "inbound" && convos[key].lastConfirmationAt
          && new Date(m.created_at) > new Date(convos[key].lastConfirmationAt)) {
        convos[key].hasInboundReplyAfterConfirmation = true;
      }
    });
    const list = Object.values(convos).sort((a,b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at));
    setConversations(list);
  }, [allMessages, clients, leadsByPhone]);

  // When a conversation is opened, pull its bot_conversation row to know if AI is paused (manual_override)
  useEffect(() => {
    setConvId(null);
    setAiOn(true);
    if (!selectedClient?.phone) return;
    const targetPk = phoneKey(selectedClient.phone);
    (async () => {
      // Look up the conv by the LAST 10 DIGITS of the phone — the previous
      // `limit(50)` hack only checked the first 50 conversations and silently
      // missed any conv with id > 50, which led to a NEW row being created
      // when the user toggled AI off and the bot continuing to read the
      // original row's `manual_override=false`. Match all phone formats.
      const last10 = (selectedClient.phone || "").replace(/\D/g, "").slice(-10);
      if (!last10) return;
      const { data } = await supabase
        .from("bot_conversations")
        .select("id,manual_override,phone")
        .ilike("phone", `%${last10}%`)
        .limit(5);
      const conv = (data || []).find(c => phoneKey(c.phone) === targetPk) || data?.[0];
      if (conv) {
        setConvId(conv.id);
        setAiOn(!conv.manual_override);
      }
    })();
  }, [selectedClient?.phone]);

  const toggleAi = async () => {
    const next = !aiOn;
    setAiOn(next);
    const last10 = (selectedClient.phone || "").replace(/\D/g, "").slice(-10);
    const overrideValue = !next;                                          // off = true (override active)
    const overrideAt = overrideValue ? new Date().toISOString() : null;

    if (last10) {
      // Update EVERY row that matches this phone (any digit format). Some
      // phones legitimately have multiple bot_conversations rows from old
      // bugs / lead+client overlap — updating them all guarantees the bot
      // sees the same state regardless of which row it reads.
      const { data: updated } = await supabase
        .from("bot_conversations")
        .update({ manual_override: overrideValue, manual_override_at: overrideAt })
        .ilike("phone", `%${last10}%`)
        .select("id");
      if (updated?.length) {
        // Track the newest id so future toggles keep referencing the same row
        const newestId = Math.max(...updated.map(r => r.id));
        setConvId(newestId);
        return;
      }
    }

    // No row existed yet — create one
    const { data: created } = await supabase.from("bot_conversations").insert({
      phone: selectedClient.phone,
      client_id: selectedClient.id || null,
      status: "active",
      manual_override: overrideValue,
      manual_override_at: overrideAt,
    }).select().single();
    if (created) setConvId(created.id);
  };

  const runReengagementPreview = async () => {
    setReengLoading(true);
    setReengPreview(null);
    setReengResult(null);
    const { data, error } = await supabase.functions.invoke("send-reengagement", { body: { dry_run: true } });
    setReengLoading(false);
    if (error) { setReengPreview({ error: error.message }); return; }
    setReengPreview(data);
  };

  const runReengagementSend = async () => {
    setReengSending(true);
    setReengResult(null);
    const { data, error } = await supabase.functions.invoke("send-reengagement", { body: { ignore_enabled: true } });
    setReengSending(false);
    if (error) { setReengResult({ error: error.message }); return; }
    setReengResult(data);
  };

  useEffect(() => {
    if (selectedClient) {
      const targetPk = phoneKey(selectedClient.phone);
      const targetConvoId = selectedClient._conversationId;
      const msgs = allMessages.filter(m => {
        if (targetPk) {
          const clientPhoneOnMsg = m.direction === "outbound" ? m.to_number : m.from_number;
          const mPk = phoneKey(clientPhoneOnMsg || "");
          if (mPk && mPk === targetPk) return true;
          if (phoneKey(m.from_number || "") === targetPk) return true;
          if (phoneKey(m.to_number || "") === targetPk) return true;
        }
        if (selectedClient.id && m.client_id === selectedClient.id) return true;
        // ★ Conversation-id fallback — when neither phone nor client_id match
        // (e.g. "Unknown" entries that previously couldn't be opened), match
        // by the bot conversation_id so the thread still renders.
        if (targetConvoId && m._conversationId === targetConvoId) return true;
        return false;
      }).sort((a,b) => new Date(a.created_at) - new Date(b.created_at));

      // ★ Collapse adjacent identical INBOUND messages (impatient clients
      // who hit send 3x get rendered as one bubble with "(×3)" indicator).
      // Belt-and-suspenders — server-side dedup should prevent these from
      // hitting the DB but this UI layer catches any stragglers.
      const collapsed = [];
      for (const m of msgs) {
        const prev = collapsed[collapsed.length - 1];
        if (
          prev &&
          prev.direction === "inbound" &&
          m.direction === "inbound" &&
          (prev.body || "").trim() === (m.body || "").trim() &&
          (new Date(m.created_at) - new Date(prev.created_at)) < 5 * 60_000
        ) {
          prev._dupCount = (prev._dupCount || 1) + 1;
          prev.created_at = m.created_at; // latest send time
        } else {
          collapsed.push({ ...m });
        }
      }
      setSelectedMessages(collapsed);
      msgs.filter(m => m.direction === "inbound" && !m.read).forEach(m => {
        if (m._botId) {
          supabase.from("bot_messages").update({ read: true }).eq("id", m._botId).then(() => {});
        } else {
          supabase.from("messages").update({ read: true }).eq("id", m.id).then(() => {});
        }
      });
    }
  }, [selectedClient, allMessages]);

  const matchesSearch = (c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.client_name || "").toLowerCase().includes(s) || (c.phone || "").includes(s);
  };

  // Split into two groups so the sidebar can render an "UNREAD" header,
  // then a "READ" header, with each group sorted by most-recent message.
  // When the user clicks the Unread filter, the read group is hidden.
  const totalUnreadCount = conversations.reduce((n, c) => n + (c.unread || 0), 0);
  const sortByRecent = (a, b) =>
    new Date(b.lastMessage?.created_at || 0) - new Date(a.lastMessage?.created_at || 0);

  // ★ Bucket membership — buckets are NOT mutually exclusive (a confirmation
  //   conv for an existing client appears in both Active and Confirmations).
  //   Archive is exclusive though: once archived, it doesn't show anywhere
  //   else. "blocked" (set by STOP opt-out detection) is treated as archived.
  const isArchivedConv = (c) => c.conv_status === "archived" || c.conv_status === "blocked";

  const inBucket = (c, bucket) => {
    if (isArchivedConv(c)) return bucket === "archive";
    switch (bucket) {
      case "all":           return true;
      case "confirmations": return !!c.hasRecentConfirmation;
      case "active":        return !!c.client_id;
      case "leads":         return !c.client_id; // any non-client (lead_id set OR unknown sender)
      case "archive":       return false; // archived already returned above
      default:              return false;
    }
  };

  // Live counts for each tab badge
  const catCounts = conversations.reduce((acc, c) => {
    if (isArchivedConv(c)) {
      acc.archive++;
    } else {
      acc.all++;
      if (c.hasRecentConfirmation) acc.confirmations++;
      if (c.client_id) acc.active++;
      else acc.leads++;
    }
    return acc;
  }, { all: 0, leads: 0, active: 0, confirmations: 0, archive: 0 });

  const matchesCategory = (c) => inBucket(c, categoryFilter);

  const unreadConvos = conversations.filter(c => c.unread > 0 && matchesCategory(c) && matchesSearch(c)).sort(sortByRecent);
  const readConvos   = conversations.filter(c => !c.unread && matchesCategory(c) && matchesSearch(c)).sort(sortByRecent);
  const visibleSections = readFilter === "unread"
    ? [{ label: null, items: unreadConvos }]
    : [
        { label: `UNREAD · ${unreadConvos.length}`, items: unreadConvos },
        { label: `READ · ${readConvos.length}`,   items: readConvos   },
      ].filter(s => s.items.length > 0);
  const filteredConvos = visibleSections.flatMap(s => s.items); // kept for any legacy refs

  const send = async () => {
    if (!input.trim() || !selectedClient?.phone) {
      setError("No phone number");
      return;
    }
    setSending(true);
    setError("");
    try {
      // Always send BOTH phone and client_id (if available) - server uses whichever works
      const body = {
        message: input,
        phone: selectedClient.phone,
      };
      if (selectedClient.id && Number.isFinite(+selectedClient.id)) {
        body.client_id = +selectedClient.id;
      }
      console.log("[MessagesInbox] sending:", body);

      const response = await supabase.functions.invoke("send-lead-sms", { body });
      let data = response.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch (e) {}
      }
      console.log("[MessagesInbox] response:", { data, error: response.error });

      // The function returns 200 with { ok: true } on success or
      // { ok: false, error } on a known error. Network-level / unknown
      // errors come back via response.error.
      if (data?.ok === true || data?.message_id) {
        setInput("");
      } else if (data?.error) {
        setError("Error: " + data.error);
      } else if (response.error) {
        // Try to extract from various places SDK might stash it
        let errMsg = response.error.message || "Send failed";
        if (response.error.context?.error) errMsg = response.error.context.error;
        try {
          const ctx = response.error.context;
          if (ctx?.body && typeof ctx.body === "string") {
            const parsed = JSON.parse(ctx.body);
            if (parsed.error) errMsg = parsed.error;
          }
        } catch (_) {}
        setError("Error: " + errMsg);
      } else {
        setError("Unexpected response from server");
      }
    } catch (e) {
      setError("Network error: " + e.message);
    } finally {
      setSending(false);
    }
  };

  const startNewConversation = async () => {
    let client = null;
    if (newFromClient) {
      client = clients.find(c => c.id === +newFromClient);
    } else if (newPhone && newName) {
      const newId = Date.now();
      const newClientData = {
        id: newId, name: newName, phone: newPhone, status: "active",
        tags: ["SMS Contact"], spent: 0, visits: 0, noshow: 0,
        card_on_file: false, gfe_status: "pending", loyalty_pts: 0,
      };
      const { error } = await supabase.from("clients").insert([newClientData]);
      if (error) { setError("Failed: " + error.message); return; }
      client = { ...newClientData, cardOnFile: false, gfeStatus: "pending", loyaltyPts: 0 };
    }
    if (client) {
      setSelectedClient(client);
      setShowNew(false);
      setNewPhone(""); setNewName(""); setNewFromClient(""); setNewSearchText("");
    }
  };

  const applyTemplate = (tpl) => {
    const text = tpl.text.replace("{name}", selectedClient?.name?.split(" ")[0] || "there");
    setInput(text);
    setShowTemplates(false);
  };

  return (
    <div style={{display:"flex",height:"calc(100vh - 140px)",background:C.surface,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>
      <div style={{width:320,borderRight:"1px solid "+C.border,display:"flex",flexDirection:"column",background:C.light}}>
        <div style={{padding:"14px 16px",borderBottom:"1px solid "+C.border,background:C.goldBg}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
              <div style={{fontSize:16,fontWeight:700,color:C.gold,fontFamily:S}}>💬 Messages</div>
              {totalUnreadCount > 0 && (
                <div style={{background:C.red,color:"#fff",borderRadius:50,padding:"2px 10px",fontSize:11,fontWeight:800,minWidth:24,textAlign:"center"}}>{totalUnreadCount}</div>
              )}
            </div>
            <div style={{display:"flex",gap:6}}>
              <button onClick={()=>{setShowReeng(true);setReengPreview(null);setReengResult(null);runReengagementPreview();}} style={{padding:"6px 10px",background:"#fff",color:"#7c3aed",border:"1px solid #7c3aed40",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>🔔 Re-engage</button>
              <button onClick={()=>setShowNew(true)} style={{padding:"6px 12px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ New</button>
            </div>
          </div>
          {/* ★ Category tabs — owner asked for these so the firehose of
              messages is broken into "🆕 New Leads", "👥 Active Clients",
              "📅 Confirmations" (any conv with a daily_confirmation in the
              last 48h — lets her review confirmation replies in one go),
              and "🗄️ Archive" (STOP opt-outs land here automatically).
              "All" hides archived/blocked so it stays clean. */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3, 1fr)",gap:5,marginBottom:6}}>
            {[
              { k: "all",           label: "All",              emoji: "📥" },
              { k: "confirmations", label: "Confirmations",    emoji: "📅" },
              { k: "leads",         label: "New Leads",        emoji: "🆕" },
              { k: "active",        label: "Active",           emoji: "👥" },
              { k: "archive",       label: "Archive",          emoji: "🗄️" },
            ].map(t => {
              const active = categoryFilter === t.k;
              const count = catCounts[t.k] || 0;
              // Highlight Confirmations tab when there's pending work
              const isConfirmations = t.k === "confirmations";
              return (
                <button
                  key={t.k}
                  onClick={()=>setCategoryFilter(t.k)}
                  style={{
                    padding:"6px 4px",
                    background: active ? "#fff" : (isConfirmations && count > 0 ? "rgba(37,99,235,0.06)" : "transparent"),
                    color: active ? (isConfirmations ? C.blue : C.gold) : C.muted,
                    border: "1px solid "+(active ? (isConfirmations ? C.blue : C.gold) : C.border),
                    borderRadius: 7,
                    fontSize: 10.5,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: F,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 3,
                    whiteSpace: "nowrap",
                  }}
                >
                  <span>{t.emoji}</span><span>{t.label}</span>
                  <span style={{fontWeight:600,opacity:0.7}}>({count})</span>
                </button>
              );
            })}
          </div>
          {/* Filter pills: All vs Unread. Counts are live. */}
          <div style={{display:"flex",gap:6,marginBottom:10}}>
            <button onClick={()=>setReadFilter("all")} style={{flex:1,padding:"7px 10px",background:readFilter==="all"?"#fff":"transparent",color:readFilter==="all"?C.gold:C.muted,border:"1px solid "+(readFilter==="all"?C.gold:C.border),borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>
              All ({unreadConvos.length + readConvos.length})
            </button>
            <button onClick={()=>setReadFilter("unread")} style={{flex:1,padding:"7px 10px",background:readFilter==="unread"?C.red:"transparent",color:readFilter==="unread"?"#fff":(unreadConvos.length>0?C.red:C.muted),border:"1px solid "+(unreadConvos.length>0?C.red:C.border),borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
              🔴 Unread ({unreadConvos.length})
            </button>
          </div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search..." style={{width:"100%",padding:"8px 12px",background:"#fff",border:"1px solid "+C.border,borderRadius:7,fontSize:12,outline:"none",boxSizing:"border-box",fontFamily:F,color:C.text}} />
        </div>
        <div style={{flex:1,overflowY:"auto"}}>
          {visibleSections.every(s => s.items.length === 0) && (
            <div style={{padding:"40px 20px",textAlign:"center",color:C.muted,fontSize:12,lineHeight:1.6}}>
              {readFilter === "unread" ? "🎉 No unread messages!"
                : categoryFilter === "confirmations" ? <>📅 No confirmation flows in the last 48h.<br/><span style={{fontSize:11,opacity:0.7}}>Daily reminders go out at 9 AM ET — check back tomorrow.</span></>
                : categoryFilter === "leads" ? <>🆕 No new leads right now.<br/><span style={{fontSize:11,opacity:0.7}}>New form/Facebook leads land here.</span></>
                : categoryFilter === "active" ? <>👥 No active client conversations.</>
                : categoryFilter === "archive" ? <>🗄️ Nothing archived.<br/><span style={{fontSize:11,opacity:0.7}}>STOP opt-outs auto-archive here.</span></>
                : <>No conversations yet.<br/>Click + New to start!</>}
            </div>
          )}
          {visibleSections.map((section, sIdx) => (
            <div key={section.label || "only"}>
              {section.label && (
                <div style={{padding:"8px 14px 4px",fontSize:9,fontWeight:800,color:C.muted,letterSpacing:1.2,textTransform:"uppercase",background:C.light,borderTop:sIdx>0?"1px solid "+C.border:"none"}}>
                  {section.label}
                </div>
              )}
              {section.items.map(c => (
                <div key={c.key} onClick={()=>{
                  // ★ Always set selectedClient even if there's no client_id
                  // and no phone — we now also carry the conversation_id, so
                  // the right pane can filter messages by that as a fallback.
                  const existing = c.client_id ? clients.find(cl => cl.id === c.client_id) : null;
                  if (existing) {
                    setSelectedClient({ ...existing, _conversationId: c.conversationId });
                  } else {
                    setSelectedClient({
                      id: c.client_id || null,
                      name: c.client_name,
                      phone: c.phone || "",
                      _conversationId: c.conversationId || null,
                    });
                  }
                }} style={{padding:"12px 14px",borderBottom:"1px solid "+C.border,cursor:"pointer",background:selectedClient && (selectedClient.id===c.client_id || selectedClient._conversationId===c.conversationId)?C.goldBg:(c.unread>0?"rgba(192,57,43,0.04)":"transparent"),borderLeft:selectedClient && (selectedClient.id===c.client_id || selectedClient._conversationId===c.conversationId)?"3px solid "+C.gold:(c.unread>0?"3px solid "+C.red:"3px solid transparent")}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:38,height:38,borderRadius:"50%",background:C.goldBg,border:"1px solid "+C.borderGold,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:C.gold,flexShrink:0}}>{/^[+\d() -]/.test(c.client_name || "") ? "📱" : (c.client_name || "?").split(" ").map(n=>n[0]).join("").substring(0,2).toUpperCase()}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:2,gap:4}}>
                        <div style={{fontSize:13,fontWeight:c.unread>0?800:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.client_name}</div>
                        <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                          {/* ★ Confirmation status chip — appears on convs with
                              a daily_confirmation in the last 48h. Helps staff
                              triage the Confirmations tab: blue = client replied,
                              grey = still pending. */}
                          {c.hasRecentConfirmation && (
                            c.hasInboundReplyAfterConfirmation ? (
                              <span title="Client replied to the confirmation reminder" style={{fontSize:9,fontWeight:800,color:C.blue,background:"rgba(37,99,235,0.10)",padding:"1px 6px",borderRadius:5,letterSpacing:"0.04em"}}>✓ REPLIED</span>
                            ) : (
                              <span title="Confirmation reminder sent — no reply yet" style={{fontSize:9,fontWeight:800,color:C.muted,background:C.light,padding:"1px 6px",borderRadius:5,letterSpacing:"0.04em"}}>⏳ PENDING</span>
                            )
                          )}
                          <div style={{fontSize:10,color:C.muted}}>{timeAgo(c.lastMessage.created_at)}</div>
                        </div>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div style={{fontSize:11,color:c.unread>0?C.text:C.muted,fontWeight:c.unread>0?600:400,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:180}}>
                          {c.lastMessage.direction==="outbound" ? "You: " : ""}{cleanBody(c.lastMessage.body)}
                        </div>
                        {c.unread > 0 && <div style={{background:C.red,color:"#fff",borderRadius:10,padding:"1px 7px",fontSize:10,fontWeight:800,minWidth:16,textAlign:"center"}}>{c.unread}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div style={{flex:1,display:"flex",flexDirection:"column"}}>
        {!selectedClient ? (
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12,color:C.muted}}>
            <div style={{fontSize:48,opacity:0.3}}>💬</div>
            <div style={{fontSize:14,fontWeight:600}}>Select a conversation</div>
            <div style={{fontSize:12}}>or click + New to start a new one</div>
          </div>
        ) : (
          <>
            <div style={{padding:"14px 18px",borderBottom:"1px solid "+C.border,background:C.goldBg,display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:42,height:42,borderRadius:"50%",background:C.goldBg,border:"2px solid "+C.borderGold,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,color:C.gold}}>{/^[+\d() -]/.test(selectedClient.name || "") ? "📱" : (selectedClient.name||"?").split(" ").map(n=>n[0]).join("").substring(0,2).toUpperCase()}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:700,color:C.text,fontFamily:S}}>{selectedClient.name}</div>
                <div style={{fontSize:11,color:C.muted}}>{formatPhone(selectedClient.phone)}</div>
              </div>
              {/* ★ OPEN PATIENT — quick jump to the full client file while
                  you're chatting. Resolves the client id from selectedClient,
                  or falls back to a phone match against the clients list, so
                  it works even when the conversation only had a phone. Hidden
                  for true non-clients (leads / unknown numbers). */}
              {(() => {
                const last10 = (selectedClient?.phone || "").replace(/\D/g, "").slice(-10);
                const resolvedId = selectedClient?.id
                  || (last10 ? (clients || []).find(c => (c.phone || "").replace(/\D/g, "").slice(-10) === last10)?.id : null);
                if (!resolvedId || !openClientProfile) return null;
                return (
                  <button
                    onClick={() => openClientProfile(resolvedId)}
                    title="Open this client's full patient file"
                    style={{
                      padding:"6px 12px",
                      background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",
                      color:"#fff", border:"none", borderRadius:999,
                      fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:F,
                      marginRight:6, whiteSpace:"nowrap",
                    }}
                  >
                    👤 Open Patient
                  </button>
                );
              })()}
              {/* ★ Archive / Unarchive button — owner asked for an archive
                  bucket in the inbox. Click flips the conversation's
                  status in DB; the inbox re-categorises via realtime. */}
              {(() => {
                const currentConv = conversations.find(c => c.key === (phoneKey(selectedClient?.phone) || ("conv_"+convId)));
                const isArchived = currentConv?.conv_status === "archived";
                return (
                  <button
                    onClick={async () => {
                      if (!convId) return;
                      const next = isArchived ? "active" : "archived";
                      try {
                        await supabase.from("bot_conversations")
                          .update({ status: next })
                          .eq("id", convId);
                        // Optimistic UI update so the tab counts react immediately
                        setConversations(cs => cs.map(c => c.conversationId === convId ? { ...c, conv_status: next } : c));
                        if (next === "archived") setSelectedClient(null);
                      } catch (e) {
                        console.error("archive toggle failed:", e);
                      }
                    }}
                    title={isArchived ? "Move back to the active inbox" : "Hide this conversation from the main inbox (Archive tab keeps it accessible)"}
                    style={{
                      padding:"6px 12px",
                      background: isArchived ? C.greenL : C.light,
                      color: isArchived ? C.green : C.muted,
                      border: "1px solid "+(isArchived ? C.green+"40" : C.border),
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: F,
                      marginRight: 6,
                    }}
                  >
                    {isArchived ? "↩️ Unarchive" : "🗄️ Archive"}
                  </button>
                );
              })()}
              <button
                onClick={toggleAi}
                title={aiOn ? "Click to PAUSE the AI bot for this conversation — only your manual replies will go out." : "Click to RESUME the AI bot — it will auto-reply to this client again."}
                style={{
                  padding:"6px 12px",
                  background: aiOn ? "linear-gradient(135deg, #2563eb, #1d4ed8)" : C.light,
                  color: aiOn ? "#fff" : C.muted,
                  border: aiOn ? "1px solid #1d4ed8" : "1px solid "+C.border,
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: F,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:aiOn?"#86efac":"#9ca3af"}}/>
                🤖 AI Bot: {aiOn ? "ON" : "OFF"}
              </button>
            </div>
            <div ref={scrollRef} style={{flex:1,overflowY:"auto",padding:20,display:"flex",flexDirection:"column",gap:8,background:"#fafaf7"}}>
              {selectedMessages.length === 0 && <div style={{textAlign:"center",color:C.muted,fontSize:12,padding:40}}>No messages yet. Send the first one!</div>}
              {selectedMessages.map(m => {
                const isOut = m.direction === "outbound";
                const sourceBadge = {
                  reengagement:  { label: "🔔 Re-engagement", color: "#8e44ad" },
                  cold_outreach: { label: "📣 Cold outreach", color: "#b48c3c" },
                  confirmation:  { label: "📅 Confirmation",  color: "#16a34a" },
                  staff:         { label: "👤 Staff",         color: "#64748b" },
                  system:        { label: "⚙️ Auto",          color: "#94a3b8" },
                  ai:            { label: "🤖 Sophia",        color: "#2563eb" },
                }[m._source] || null;
                return (
                  <div key={m.id} style={{display:"flex",flexDirection:isOut?"row-reverse":"row",marginBottom:2}}>
                    <div style={{maxWidth:"75%",padding:"10px 14px",background:isOut?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":"#fff",color:isOut?"#fff":C.text,borderRadius:isOut?"16px 4px 16px 16px":"4px 16px 16px 16px",fontSize:13.5,lineHeight:1.55,boxShadow:isOut?"0 2px 6px rgba(180,140,60,0.25)":"0 1px 2px rgba(0,0,0,0.06)",border:m.direction==="inbound"?"1px solid "+C.border:"none",whiteSpace:"pre-wrap"}}>
                      {sourceBadge && (
                        <div style={{display:"inline-block",fontSize:9,padding:"2px 8px",borderRadius:10,background:isOut?"rgba(255,255,255,0.22)":sourceBadge.color+"18",color:isOut?"#fff":sourceBadge.color,fontWeight:800,marginBottom:6,letterSpacing:"0.04em",textTransform:"uppercase"}}>{sourceBadge.label}</div>
                      )}
                      <div>{cleanBody(m.body)}</div>
                      <div style={{fontSize:10,opacity:0.7,marginTop:6,whiteSpace:"nowrap",display:"flex",gap:6,alignItems:"center"}}>
                        <span>{bubbleTime(m.created_at)}{isOut && m.sent_by_name ? " · " + m.sent_by_name.split(" ")[0] : ""}</span>
                        {m._dupCount > 1 && (
                          <span style={{padding:"1px 6px",background:isOut?"rgba(255,255,255,0.25)":C.muted+"22",color:isOut?"#fff":C.muted,borderRadius:8,fontSize:9,fontWeight:700}}>×{m._dupCount} sent</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {error && <div style={{padding:"8px 18px",background:C.redL,color:C.red,fontSize:12,fontWeight:600,borderTop:"1px solid "+C.red+"30"}}>{error}</div>}
            {showTemplates && (
              <div style={{padding:"10px 16px",borderTop:"1px solid "+C.border,background:C.light,maxHeight:150,overflowY:"auto"}}>
                {QUICK_TEMPLATES.map((t,i) => (
                  <div key={i} onClick={()=>applyTemplate(t)} style={{padding:"7px 10px",background:"#fff",border:"1px solid "+C.border,borderRadius:6,marginBottom:4,cursor:"pointer",fontSize:11}}>
                    <div style={{fontWeight:700,color:C.text,marginBottom:2}}>{t.label}</div>
                    <div style={{color:C.muted,fontSize:10}}>{t.text.substring(0,70)}...</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{padding:"12px 16px",borderTop:"1px solid "+C.border,background:"#fff"}}>
              <div style={{display:"flex",gap:6,marginBottom:8}}>
                <button onClick={()=>setShowTemplates(!showTemplates)} style={{padding:"5px 10px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>Templates</button>
              </div>
              <div style={{display:"flex",gap:8}}>
                <textarea value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}} placeholder="Type a message..." rows={2} style={{flex:1,padding:"10px 12px",border:"1px solid "+C.border,borderRadius:8,fontSize:13,fontFamily:F,resize:"none",outline:"none",background:"#fff",color:C.text}} />
                <button onClick={send} disabled={sending||!input.trim()} style={{padding:"10px 20px",background:(sending||!input.trim())?C.light:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:(sending||!input.trim())?C.muted:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:(sending||!input.trim())?"not-allowed":"pointer",fontFamily:F}}>{sending?"...":"Send"}</button>
              </div>
            </div>
          </>
        )}
      </div>
      {showReeng && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:640,padding:24,maxHeight:"85vh",overflowY:"auto"}}>
            <div style={{fontSize:18,fontWeight:700,color:"#7c3aed",fontFamily:S,marginBottom:6}}>🔔 Re-engagement Preview</div>
            <div style={{fontSize:12,color:C.muted,marginBottom:18}}>Scan all clients who match your rules. Nothing is sent until you click <strong>Send to all</strong> below.</div>

            {reengLoading && <div style={{padding:30,textAlign:"center",color:C.muted,fontSize:13}}>Scanning your clients...</div>}

            {reengPreview && reengPreview.error && (
              <div style={{padding:12,background:C.redL,color:C.red,borderRadius:8,fontSize:12,fontWeight:600,marginBottom:14}}>Error: {reengPreview.error}</div>
            )}

            {reengPreview && !reengPreview.error && (
              <>
                <div style={{padding:"12px 14px",background:reengPreview.queued>0?C.goldBg:C.light,borderRadius:10,border:"1px solid "+(reengPreview.queued>0?C.borderGold:C.border),marginBottom:14}}>
                  <div style={{fontSize:22,fontWeight:800,color:reengPreview.queued>0?C.gold:C.muted,fontFamily:S}}>
                    {reengPreview.queued} client{reengPreview.queued===1?"":"s"} ready for re-engagement
                  </div>
                  <div style={{fontSize:11,color:C.muted,marginTop:3}}>Scanned {reengPreview.scanned} past appointments matching your rules</div>
                </div>

                {(reengPreview.preview || []).length === 0 ? (
                  <div style={{padding:30,textAlign:"center",color:C.muted,fontSize:12,background:C.light,borderRadius:10,border:"1px dashed "+C.border,marginBottom:14}}>
                    No clients to re-engage right now. Try again in a few days as more clients hit their treatment window.
                  </div>
                ) : (
                  <div style={{maxHeight:340,overflowY:"auto",border:"1px solid "+C.border,borderRadius:10,marginBottom:14}}>
                    {(reengPreview.preview || []).map((p, i) => (
                      <div key={i} style={{padding:"10px 14px",borderBottom:i<reengPreview.preview.length-1?"1px solid "+C.border:"none",display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:32,height:32,borderRadius:"50%",background:C.goldBg,border:"1px solid "+C.borderGold,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:C.gold,flexShrink:0}}>
                          {(p.client_name || "?").split(" ").map(n=>n[0]).join("").substring(0,2).toUpperCase()}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:700,color:C.text}}>{p.client_name}</div>
                          <div style={{fontSize:11,color:C.muted}}>{formatPhone(p.phone)} · {p.treatment}</div>
                        </div>
                        <div style={{fontSize:11,color:C.gold,fontWeight:700,whiteSpace:"nowrap"}}>{p.days_since}d ago</div>
                      </div>
                    ))}
                  </div>
                )}

                {reengResult && reengResult.error && (
                  <div style={{padding:12,background:C.redL,color:C.red,borderRadius:8,fontSize:12,fontWeight:600,marginBottom:14}}>Send error: {reengResult.error}</div>
                )}
                {reengResult && !reengResult.error && (
                  <div style={{padding:12,background:C.greenL,color:C.green,borderRadius:8,fontSize:12,fontWeight:700,marginBottom:14}}>
                    ✓ Sent {reengResult.sent} of {reengResult.total ?? reengResult.queued} messages. {reengResult.failed>0?`(${reengResult.failed} failed)`:""}
                  </div>
                )}

                <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                  <button onClick={()=>setShowReeng(false)} style={{padding:"10px 18px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:F}}>Close</button>
                  <button
                    onClick={runReengagementSend}
                    disabled={reengSending || reengPreview.queued === 0 || reengResult}
                    style={{padding:"10px 22px",background:(reengSending||reengPreview.queued===0||reengResult)?C.light:"linear-gradient(135deg,#7c3aed,#5b21b6)",color:(reengSending||reengPreview.queued===0||reengResult)?C.muted:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:(reengSending||reengPreview.queued===0||reengResult)?"not-allowed":"pointer",fontFamily:F}}
                  >
                    {reengSending ? "Sending..." : reengResult ? "✓ Sent" : `Send to all ${reengPreview.queued}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showNew && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:460,padding:24}}>
            <div style={{fontSize:17,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:16}}>Start New Conversation</div>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:6}}>Choose from Contacts</label>
              {/* Live search instead of a 2500-row dropdown. As the user types,
                  show up to 8 matches with phone + email; clicking picks the
                  client. Selecting clears the search so the modal can show
                  the chosen card. */}
              <input
                type="text"
                value={newSearchText}
                onChange={e => { setNewSearchText(e.target.value); setNewFromClient(""); }}
                placeholder="🔍 Type name, phone, or email..."
                style={{width:"100%",padding:"10px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:13,fontFamily:F,outline:"none",boxSizing:"border-box",color:C.text}}
              />
              {newFromClient ? (() => {
                const chosen = clients.find(c => String(c.id) === String(newFromClient));
                if (!chosen) return null;
                return (
                  <div style={{marginTop:8,padding:"10px 12px",background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:C.text}}>{chosen.name}</div>
                      <div style={{fontSize:11,color:C.muted}}>{formatPhone(chosen.phone)}{chosen.email ? " · " + chosen.email : ""}</div>
                    </div>
                    <button onClick={() => { setNewFromClient(""); setNewSearchText(""); }} style={{padding:"4px 10px",background:"#fff",color:C.muted,border:"1px solid "+C.border,borderRadius:6,fontSize:11,cursor:"pointer",fontFamily:F}}>Change</button>
                  </div>
                );
              })() : newSearchText.trim().length >= 1 && (() => {
                const q = newSearchText.toLowerCase().trim();
                const qDigits = q.replace(/\D/g,"");
                // ★ Guard against empty-string includes — `"".includes("")` is
                // TRUE, so without this guard EVERY client with a phone would
                // match when the search query has no digits (e.g. "bani"),
                // which is why the modal was showing 8 random clients.
                const clientMatches = clients.filter(c => {
                  if (!c.phone) return false;
                  if ((c.name || "").toLowerCase().includes(q)) return true;
                  if ((c.email || "").toLowerCase().includes(q)) return true;
                  if (qDigits.length >= 3 && (c.phone || "").replace(/\D/g,"").includes(qDigits)) return true;
                  return false;
                }).slice(0, 8);
                const leadMatches = newLeadMatches.slice(0, 8);

                if (clientMatches.length === 0 && leadMatches.length === 0 && !newLeadSearching) {
                  return <div style={{marginTop:8,padding:"10px 12px",fontSize:12,color:C.muted,textAlign:"center"}}>No matches — try adding them as a new contact below ↓</div>;
                }

                return (
                  <div style={{marginTop:6,maxHeight:280,overflowY:"auto",border:"1px solid "+C.border,borderRadius:8,background:"#fff"}}>
                    {clientMatches.length > 0 && (
                      <>
                        <div style={{padding:"4px 12px",fontSize:9,fontWeight:800,color:C.gold,background:C.goldBg,textTransform:"uppercase",letterSpacing:"0.08em"}}>👤 Clients</div>
                        {clientMatches.map(c => (
                          <button key={"c"+c.id} onClick={() => { setNewFromClient(String(c.id)); }} style={{display:"block",width:"100%",padding:"10px 12px",background:"none",border:"none",borderBottom:"1px solid "+C.border,textAlign:"left",cursor:"pointer",fontFamily:F}}>
                            <div style={{fontSize:13,fontWeight:600,color:C.text}}>{c.name}</div>
                            <div style={{fontSize:11,color:C.muted}}>{formatPhone(c.phone)}{c.email ? " · " + c.email : ""}</div>
                          </button>
                        ))}
                      </>
                    )}
                    {leadMatches.length > 0 && (
                      <>
                        <div style={{padding:"4px 12px",fontSize:9,fontWeight:800,color:"#2563eb",background:"rgba(37,99,235,0.08)",textTransform:"uppercase",letterSpacing:"0.08em"}}>🔥 Leads</div>
                        {leadMatches.map(l => {
                          const name = l.full_name || l.first_name || "Unnamed Lead";
                          return (
                            <button
                              key={"l"+l.id}
                              onClick={() => {
                                // Leads are messaged by phone, not client_id
                                setNewPhone(l.phone || "");
                                setNewName(name);
                                setNewFromClient("");
                                setNewSearchText("");
                              }}
                              style={{display:"block",width:"100%",padding:"10px 12px",background:"none",border:"none",borderBottom:"1px solid "+C.border,textAlign:"left",cursor:"pointer",fontFamily:F}}
                            >
                              <div style={{fontSize:13,fontWeight:600,color:C.text}}>{name}{l.source ? <span style={{fontSize:9,fontWeight:700,padding:"1px 6px",background:"rgba(37,99,235,0.12)",color:"#2563eb",borderRadius:6,marginLeft:6,letterSpacing:"0.03em"}}>{l.source}</span> : null}</div>
                              <div style={{fontSize:11,color:C.muted}}>{formatPhone(l.phone)}{l.email ? " · " + l.email : ""}</div>
                            </button>
                          );
                        })}
                      </>
                    )}
                    {newLeadSearching && (
                      <div style={{padding:"6px 12px",fontSize:11,color:C.muted,textAlign:"center"}}>Searching leads…</div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div style={{textAlign:"center",color:C.muted,fontSize:11,margin:"14px 0"}}>OR</div>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:6}}>Name</label>
                <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="Jane Smith" style={{width:"100%",padding:"10px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:13,fontFamily:F,outline:"none",boxSizing:"border-box",color:C.text}} />
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:6}}>Phone</label>
                <input value={newPhone} onChange={e=>setNewPhone(e.target.value)} placeholder="+1 555 123 4567" style={{width:"100%",padding:"10px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:13,fontFamily:F,outline:"none",boxSizing:"border-box",color:C.text}} />
              </div>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setShowNew(false)} style={{padding:"10px 18px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:F}}>Cancel</button>
              <button onClick={startNewConversation} disabled={!newFromClient && (!newName || !newPhone)} style={{padding:"10px 20px",background:(!newFromClient && (!newName || !newPhone))?C.light:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:(!newFromClient && (!newName || !newPhone))?C.muted:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:(!newFromClient && (!newName || !newPhone))?"not-allowed":"pointer",fontFamily:F}}>Start</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
